import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import {
  Building2, DollarSign, Settings, Users, ChevronRight, ChevronLeft,
  Check, ArrowRight, Sparkles, TrendingUp, Mail, Loader2, CheckCircle2,
  Circle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import useIndustryProfilesList from '../hooks/useIndustryProfilesList';
import OnboardingBaselineStep from './onboarding/OnboardingBaselineStep';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'org-profile', title: 'Organization Profile', icon: Building2, desc: 'Tell us about your organization' },
  { id: 'financials', title: 'Financial Parameters', icon: DollarSign, desc: 'Set your financial baseline' },
  { id: 'defaults', title: 'Analysis Defaults', icon: Settings, desc: 'Configure analysis preferences' },
  { id: 'invite', title: 'Invite Team', icon: Users, desc: 'Bring your team onboard' },
  { id: 'baseline', title: 'Light Up', icon: Sparkles, desc: 'Populate Mission Control instantly' },
];

const QUICK_PRESETS = {
  conservative: { label: 'Conservative', icon: '🛡️', scenario: 'conservative', maturity: 35, dampening: { adoption_speed: 0.55, leadership_alignment: 0.65, operational_readiness: 0.60 } },
  moderate: { label: 'Balanced Growth', icon: '⚖️', scenario: 'moderate', maturity: 50, dampening: { adoption_speed: 0.70, leadership_alignment: 0.80, operational_readiness: 0.75 } },
  aggressive: { label: 'Aggressive Growth', icon: '🚀', scenario: 'aggressive', maturity: 65, dampening: { adoption_speed: 0.85, leadership_alignment: 0.90, operational_readiness: 0.85 } },
  enterprise: { label: 'Mature Enterprise', icon: '🏢', scenario: 'moderate', maturity: 80, dampening: { adoption_speed: 0.60, leadership_alignment: 0.75, operational_readiness: 0.90 } },
};

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, updateProfile, checkAuth } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [inviting, setInviting] = useState(false);

  const { industries: industryProfiles } = useIndustryProfilesList();

  const [form, setForm] = useState({
    organization_name: '', industry_type: 'technology', business_type: 'enterprise',
    company_size: 'large', region: 'north_america',
    total_workforce: 2000, eligible_population: 1500,
    default_currency: 'USD', fiscal_year_start: 'january',
    annual_revenue: 300000000, revenue_per_employee: 150000,
    operating_margin: 15, default_intervention_budget: 500000,
    preferred_elasticity_scenario: 'moderate', current_maturity: 50,
    dampening_factors: { adoption_speed: 0.7, leadership_alignment: 0.8, operational_readiness: 0.75 },
    selectedPreset: null,
  });

  useEffect(() => {
    if (user?.profile) {
      setForm(prev => ({
        ...prev,
        ...user.profile,
        dampening_factors: { ...prev.dampening_factors, ...user.profile?.dampening_factors },
      }));
    }
  }, [user]);

  // Load existing onboarding progress
  useEffect(() => {
    const load = async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const res = await authedFetch(`${API_URL}/api/rbac/onboarding/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.completed_steps) setCompletedSteps(data.completed_steps);
        }
      } catch { /* silent */ }
    };
    load();
  }, []);

  const saveStep = useCallback(async (stepId) => {
    try {
      const token = secureStorage.getItem('session_token');
      await authedFetch(`${API_URL}/api/rbac/onboarding/step`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: stepId })
      });
      setCompletedSteps(prev => [...new Set([...prev, stepId])]);
    } catch { /* silent */ }
  }, []);

  const handleNext = async () => {
    setSaving(true);
    try {
      // Save profile data on steps 0-2; all steps record completion.
      if (currentStep <= 2) {
        const { selectedPreset, ...profileData } = form;
        await updateProfile({ profile: profileData });
        toast.success(`${STEPS[currentStep].title} saved`);
      }
      await saveStep(STEPS[currentStep].id);
      if (currentStep < STEPS.length) {
        setCurrentStep(s => s + 1);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/rbac/users/invite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });
      if (res.ok) {
        setInvitedUsers(prev => [...prev, { email: inviteEmail.trim(), role: inviteRole }]);
        setInviteEmail('');
        toast.success(`Invitation sent to ${inviteEmail.trim()}`);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send invite');
      }
    } catch { toast.error('Error sending invite'); }
    finally { setInviting(false); }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await saveStep('invite');
      const token = secureStorage.getItem('session_token');
      await authedFetch(`${API_URL}/api/rbac/onboarding/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await checkAuth();
      localStorage.setItem('just_onboarded', 'true');
      toast.success('Setup complete!');
      navigate('/');
    } catch { toast.error('Error completing setup'); }
    finally { setSaving(false); }
  };

  const applyPreset = (key) => {
    const p = QUICK_PRESETS[key];
    setForm(prev => ({
      ...prev,
      preferred_elasticity_scenario: p.scenario,
      current_maturity: p.maturity,
      dampening_factors: { ...prev.dampening_factors, ...p.dampening },
      selectedPreset: key,
    }));
    toast.success(`Applied "${p.label}" preset`);
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: form.default_currency || 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);

  const isSummary = currentStep === STEPS.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1" data-testid="onboarding-title">
            {isSummary ? 'Setup Complete' : 'Welcome! Let\'s set up your organization'}
          </h1>
          <p className="text-slate-500 text-sm">
            {isSummary ? 'Here\'s a summary of your configuration' : `Step ${currentStep + 1} of ${STEPS.length}`}
          </p>
        </div>

        {/* Progress bar */}
        {!isSummary && (
          <div className="flex items-center gap-1 mb-8" data-testid="onboarding-progress">
            {STEPS.map((step, i) => {
              const StepIcon = step.icon;
              const isActive = i === currentStep;
              const isDone = completedSteps.includes(step.id);
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => i <= currentStep && setCurrentStep(i)}
                    disabled={i > currentStep && !isDone}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive ? 'bg-teal-600 text-white shadow-md' : ''}
                      ${isDone && !isActive ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' : ''}
                      ${!isActive && !isDone ? 'text-slate-400' : ''}
                    `}
                    data-testid={`step-${step.id}`}
                  >
                    {isDone && !isActive ? <Check size={14} /> : <StepIcon size={14} />}
                    <span className="hidden sm:inline">{step.title}</span>
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300 shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Step content */}
        <Card className="shadow-lg border-0">
          <CardContent className="pt-6">
            {/* Step 1: Organization Profile */}
            {currentStep === 0 && (
              <div className="space-y-5" data-testid="step-org-profile-content">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Building2 className="text-teal-600" size={20} />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg text-slate-900">Organization Profile</h2>
                    <p className="text-sm text-slate-500">Basic information about your organization</p>
                  </div>
                </div>

                <div>
                  <Label>Organization Name</Label>
                  <Input value={form.organization_name} onChange={(e) => setForm(f => ({ ...f, organization_name: e.target.value }))} placeholder="e.g., Acme Corp" className="mt-1" data-testid="onboard-org-name" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Industry</Label>
                    <Select value={form.industry_type} onValueChange={(v) => setForm(f => ({ ...f, industry_type: v }))}>
                      <SelectTrigger className="mt-1" data-testid="onboard-industry"><SelectValue placeholder="Select an industry…" /></SelectTrigger>
                      <SelectContent>
                        {industryProfiles.length === 0 ? (
                          <SelectItem value="other" disabled>Loading industries…</SelectItem>
                        ) : (
                          industryProfiles.map((ind) => (
                            <SelectItem key={ind.id} value={ind.id}>{ind.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Company Size</Label>
                    <Select value={form.company_size} onValueChange={(v) => setForm(f => ({ ...f, company_size: v }))}>
                      <SelectTrigger className="mt-1" data-testid="onboard-size"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="startup">Startup (&lt;50)</SelectItem>
                        <SelectItem value="small">Small (50-500)</SelectItem>
                        <SelectItem value="medium">Medium (500-2,000)</SelectItem>
                        <SelectItem value="large">Large (2,000-10,000)</SelectItem>
                        <SelectItem value="enterprise">Enterprise (&gt;10,000)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Region</Label>
                    <Select value={form.region} onValueChange={(v) => setForm(f => ({ ...f, region: v }))}>
                      <SelectTrigger className="mt-1" data-testid="onboard-region"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="north_america">North America</SelectItem>
                        <SelectItem value="europe">Europe</SelectItem>
                        <SelectItem value="asia_pacific">Asia Pacific</SelectItem>
                        <SelectItem value="latin_america">Latin America</SelectItem>
                        <SelectItem value="middle_east">Middle East & Africa</SelectItem>
                        <SelectItem value="global">Global</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Business Type</Label>
                    <Select value={form.business_type} onValueChange={(v) => setForm(f => ({ ...f, business_type: v }))}>
                      <SelectTrigger className="mt-1" data-testid="onboard-btype"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enterprise">Enterprise (B2B)</SelectItem>
                        <SelectItem value="consumer">Consumer (B2C)</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                        <SelectItem value="nonprofit">Non-Profit</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Workforce</Label>
                    <Input type="number" value={form.total_workforce} onChange={(e) => setForm(f => ({ ...f, total_workforce: parseInt(e.target.value) || 0 }))} className="mt-1" data-testid="onboard-workforce" />
                  </div>
                  <div>
                    <Label>Eligible Population</Label>
                    <Input type="number" value={form.eligible_population} onChange={(e) => setForm(f => ({ ...f, eligible_population: parseInt(e.target.value) || 0 }))} className="mt-1" data-testid="onboard-eligible" />
                    <p className="text-xs text-slate-400 mt-1">Employees eligible for training</p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Financial Parameters */}
            {currentStep === 1 && (
              <div className="space-y-5" data-testid="step-financials-content">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="text-emerald-600" size={20} />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg text-slate-900">Financial Parameters</h2>
                    <p className="text-sm text-slate-500">Used in ROI and impact calculations</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Default Currency</Label>
                    <Select value={form.default_currency} onValueChange={(v) => setForm(f => ({ ...f, default_currency: v }))}>
                      <SelectTrigger className="mt-1" data-testid="onboard-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                        <SelectItem value="AUD">AUD (A$)</SelectItem>
                        <SelectItem value="CAD">CAD (C$)</SelectItem>
                        <SelectItem value="INR">INR (₹)</SelectItem>
                        <SelectItem value="JPY">JPY (¥)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fiscal Year Start</Label>
                    <Select value={form.fiscal_year_start} onValueChange={(v) => setForm(f => ({ ...f, fiscal_year_start: v }))}>
                      <SelectTrigger className="mt-1" data-testid="onboard-fiscal"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="january">January</SelectItem>
                        <SelectItem value="april">April</SelectItem>
                        <SelectItem value="july">July</SelectItem>
                        <SelectItem value="october">October</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <TrendingUp size={16} className="text-emerald-600" /> Revenue Metrics
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Annual Revenue</Label>
                      <Input type="number" value={form.annual_revenue} onChange={(e) => setForm(f => ({ ...f, annual_revenue: parseFloat(e.target.value) || 0 }))} className="mt-1" data-testid="onboard-revenue" />
                      <p className="text-xs text-slate-500 mt-1">{formatCurrency(form.annual_revenue)}</p>
                    </div>
                    <div>
                      <Label>Revenue per Employee</Label>
                      <Input type="number" value={form.revenue_per_employee} onChange={(e) => setForm(f => ({ ...f, revenue_per_employee: parseFloat(e.target.value) || 0 }))} className="mt-1" data-testid="onboard-rev-per-emp" />
                      <p className="text-xs text-slate-500 mt-1">{formatCurrency(form.revenue_per_employee)}/employee</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <Label>Operating Margin</Label>
                      <span className="font-medium text-emerald-600">{form.operating_margin}%</span>
                    </div>
                    <Slider value={[form.operating_margin]} onValueChange={([v]) => setForm(f => ({ ...f, operating_margin: v }))} max={50} min={0} step={1} />
                  </div>
                </div>

                <div>
                  <Label>Default Intervention Budget</Label>
                  <Input type="number" value={form.default_intervention_budget} onChange={(e) => setForm(f => ({ ...f, default_intervention_budget: parseFloat(e.target.value) || 0 }))} className="mt-1" data-testid="onboard-budget" />
                  <p className="text-xs text-slate-500 mt-1">{formatCurrency(form.default_intervention_budget)}</p>
                </div>
              </div>
            )}

            {/* Step 3: Analysis Defaults */}
            {currentStep === 2 && (
              <div className="space-y-5" data-testid="step-defaults-content">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Settings className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg text-slate-900">Analysis Defaults</h2>
                    <p className="text-sm text-slate-500">Quick-start with a preset or customize below</p>
                  </div>
                </div>

                {/* Quick Presets */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Sparkles size={14} className="text-purple-500" /> Quick Start Preset
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(QUICK_PRESETS).map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => applyPreset(key)}
                        className={`p-3 rounded-lg border-2 text-left transition-all hover:shadow-md
                          ${form.selectedPreset === key ? 'border-teal-500 bg-teal-50 shadow-md' : 'border-slate-200 hover:border-slate-300'}
                        `}
                        data-testid={`onboard-preset-${key}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{preset.icon}</span>
                          <span className="font-medium text-slate-800 text-sm">{preset.label}</span>
                          {form.selectedPreset === key && <Check size={14} className="ml-auto text-teal-600" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Elasticity Scenario */}
                <div>
                  <Label>Preferred Elasticity Scenario</Label>
                  <Select value={form.preferred_elasticity_scenario} onValueChange={(v) => setForm(f => ({ ...f, preferred_elasticity_scenario: v }))}>
                    <SelectTrigger className="mt-1" data-testid="onboard-elasticity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservative</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="aggressive">Aggressive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Maturity */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <Label>Current Maturity Level</Label>
                    <span className="font-medium text-purple-600">{form.current_maturity}%</span>
                  </div>
                  <Slider value={[form.current_maturity]} onValueChange={([v]) => setForm(f => ({ ...f, current_maturity: v }))} max={100} min={10} step={5} />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Emerging</span><span>Developing</span><span>Mature</span><span>Leading</span>
                  </div>
                </div>

                {/* Dampening Factors */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-800">Dampening Factors</h4>
                  {[
                    { key: 'adoption_speed', label: 'Adoption Speed' },
                    { key: 'leadership_alignment', label: 'Leadership Alignment' },
                    { key: 'operational_readiness', label: 'Operational Readiness' },
                  ].map(f => (
                    <div key={f.key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-700">{f.label}</span>
                        <span className="text-teal-600 font-medium">{((form.dampening_factors?.[f.key] || 0.7) * 100).toFixed(0)}%</span>
                      </div>
                      <Slider value={[(form.dampening_factors?.[f.key] || 0.7) * 100]} onValueChange={([v]) => setForm(prev => ({ ...prev, dampening_factors: { ...prev.dampening_factors, [f.key]: v / 100 } }))} max={100} min={20} step={5} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Invite Team */}
            {currentStep === 3 && (
              <div className="space-y-5" data-testid="step-invite-content">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                    <Users className="text-sky-600" size={20} />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg text-slate-900">Invite Your Team</h2>
                    <p className="text-sm text-slate-500">Add team members to your organization</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
                    className="flex-1"
                    data-testid="onboard-invite-email"
                  />
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="w-[130px]" data-testid="onboard-invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="org_admin">Org Admin</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()} data-testid="onboard-invite-btn">
                    {inviting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} className="mr-1.5" />}
                    Invite
                  </Button>
                </div>

                {invitedUsers.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <p className="text-sm font-medium text-slate-700">Invited ({invitedUsers.length})</p>
                    {invitedUsers.map((inv, i) => (
                      <div key={inv.email || `inv-${i}`} className="flex items-center justify-between p-2.5 bg-emerald-50 rounded-lg border border-emerald-100">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 size={14} className="text-emerald-600" />
                          <span className="text-sm text-slate-700">{inv.email}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">{inv.role === 'org_admin' ? 'Org Admin' : 'User'}</Badge>
                      </div>
                    ))}
                  </div>
                )}

                {invitedUsers.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Mail size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No invitations sent yet</p>
                    <p className="text-xs mt-1">You can always invite people later from the Admin panel</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Light up Mission Control */}
            {currentStep === 4 && (
              <OnboardingBaselineStep
                industry={form.industry_type}
                headcount={form.total_workforce}
              />
            )}

            {/* Summary */}
            {isSummary && (
              <div className="space-y-5" data-testid="step-summary-content">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <CheckCircle2 className="text-teal-600" size={20} />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg text-slate-900">Setup Summary</h2>
                    <p className="text-sm text-slate-500">Here's what you've configured</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Org Profile */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {completedSteps.includes('org-profile') ? <CheckCircle2 size={16} className="text-teal-600" /> : <Circle size={16} className="text-slate-300" />}
                      <h3 className="font-medium text-slate-800">Organization Profile</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm ml-6">
                      <span className="text-slate-500">Name</span><span className="font-medium">{form.organization_name || '-'}</span>
                      <span className="text-slate-500">Industry</span><span className="font-medium capitalize">{form.industry_type?.replace('_', ' ')}</span>
                      <span className="text-slate-500">Size</span><span className="font-medium capitalize">{form.company_size}</span>
                      <span className="text-slate-500">Workforce</span><span className="font-medium">{form.total_workforce?.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Financials */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {completedSteps.includes('financials') ? <CheckCircle2 size={16} className="text-teal-600" /> : <Circle size={16} className="text-slate-300" />}
                      <h3 className="font-medium text-slate-800">Financial Parameters</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm ml-6">
                      <span className="text-slate-500">Currency</span><span className="font-medium">{form.default_currency}</span>
                      <span className="text-slate-500">Annual Revenue</span><span className="font-medium">{formatCurrency(form.annual_revenue)}</span>
                      <span className="text-slate-500">Operating Margin</span><span className="font-medium">{form.operating_margin}%</span>
                      <span className="text-slate-500">Budget</span><span className="font-medium">{formatCurrency(form.default_intervention_budget)}</span>
                    </div>
                  </div>

                  {/* Defaults */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {completedSteps.includes('defaults') ? <CheckCircle2 size={16} className="text-teal-600" /> : <Circle size={16} className="text-slate-300" />}
                      <h3 className="font-medium text-slate-800">Analysis Defaults</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm ml-6">
                      <span className="text-slate-500">Scenario</span><span className="font-medium capitalize">{form.preferred_elasticity_scenario}</span>
                      <span className="text-slate-500">Maturity</span><span className="font-medium">{form.current_maturity}%</span>
                      <span className="text-slate-500">Combined Dampening</span>
                      <span className="font-medium">
                        {((form.dampening_factors?.adoption_speed || 0.7) * (form.dampening_factors?.leadership_alignment || 0.8) * (form.dampening_factors?.operational_readiness || 0.75) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>

                  {/* Invitations */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {invitedUsers.length > 0 ? <CheckCircle2 size={16} className="text-teal-600" /> : <Circle size={16} className="text-slate-300" />}
                      <h3 className="font-medium text-slate-800">Team Invitations</h3>
                    </div>
                    <p className="text-sm ml-6 text-slate-600">
                      {invitedUsers.length > 0 ? `${invitedUsers.length} invitation${invitedUsers.length > 1 ? 's' : ''} sent` : 'No invitations sent (you can do this later)'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-6">
          {currentStep > 0 && !isSummary ? (
            <Button variant="ghost" onClick={() => setCurrentStep(s => s - 1)} data-testid="onboard-back">
              <ChevronLeft size={16} className="mr-1" /> Back
            </Button>
          ) : (
            <div />
          )}

          {isSummary ? (
            <Button onClick={handleComplete} disabled={saving} className="bg-teal-600 hover:bg-teal-700 px-6" data-testid="onboard-go-dashboard">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ArrowRight size={14} className="mr-2" />}
              Go to Dashboard
            </Button>
          ) : currentStep === STEPS.length - 1 ? (
            <Button onClick={handleNext} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="onboard-finish">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle2 size={14} className="mr-2" />}
              Finish Setup
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="onboard-next">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
              Continue <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </div>

        {/* Skip option */}
        {!isSummary && (
          <div className="text-center mt-3">
            <button
              onClick={handleComplete}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="onboard-skip"
            >
              Skip setup for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
